import { LockOutlined, LoginOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Form, Input } from 'antd';
import SubmitFormButton from 'common/components/submit-form-button.component';
import { AuthType } from 'config/constants/auth.constants';
import { AuthTranscription } from 'config/constants/transcription.constant';
import AuthService from 'config/services/auth.service';
import UserService from 'config/services/user.service';
import { LoginPayload } from 'config/types/auth.type';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import CartUtils from 'config/utils/cart.util';
import { getErrorMessage } from 'config/utils/errors.util';
import NavigationUtils from 'config/utils/navigation.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import UserUtils from 'config/utils/user.util';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AuthSlice } from 'store/slices/auth.slice';
import { UserSlice } from 'store/slices/user.slice';

const LoginForm: React.FC = () => {
  const dispatch = useDispatch();
  const [loginForm] = Form.useForm<LoginPayload>();
  const location = useLocation();

  const handleLoginSuccess = async (authLocation: Partial<Location>): Promise<void> => {
    dispatch(UserSlice.fetchingRefresh(true));
    try {
      const userResult = await UserService.getUserProfile();
      dispatch(UserSlice.refreshUserProfile(userResult));
      await CartUtils.reconcileCartAfterAuth();
      await AuthIntentUtils.resumeAfterAuth(authLocation);
    } finally {
      dispatch(UserSlice.fetchingRefresh(false));
    }
  };

  const handleLoginSubmit = async (formValues: LoginPayload) => {
    const authLocation: Partial<Location> = { pathname: location.pathname, search: location.search };
    dispatch(AuthSlice.fetchingRefresh(true));
    try {
      const loginResult = await AuthService.login(formValues);
      const { accessToken, refreshToken } = loginResult;
      dispatch(AuthSlice.refreshLoginData({ accessToken, refreshToken, isAuthenticated: Boolean(accessToken) }));
      if (accessToken) await handleLoginSuccess(authLocation);
    } catch (error: unknown) {
      SnackbarUtilities.error(getErrorMessage(error, AuthTranscription.UNKNOWN_LOGIN_ERROR));
    } finally {
      dispatch(AuthSlice.fetchingRefresh(false));
    }
  };

  return (
    <Form form={loginForm} name='standard-login' layout='vertical' autoComplete='on' onFinish={handleLoginSubmit}>
      <Form.Item name='email' rules={UserUtils.emailFieldRules} shouldUpdate validateFirst>
        <Input
          prefix={<UserOutlined className='site-form-item-icon' />}
          placeholder={AuthTranscription.FORM_EMAIL_PLACEHOLDER}
          name='email'
          type='email'
        />
      </Form.Item>
      <Form.Item name='password' rules={UserUtils.passwordLoginFiedRules} shouldUpdate validateFirst>
        <Input.Password
          prefix={<LockOutlined className='site-form-item-icon' />}
          placeholder={AuthTranscription.FORM_PASSWORD_PLACEHOLDER}
          name='password'
        />
      </Form.Item>
      <Form.Item>
        <Button type='link' onClick={() => NavigationUtils.handleAuthTypeAction(AuthType.RECOVER_PASS, true, null, location)}>
          {AuthTranscription.FORM_RECOVER_PASSWORD_TEXT}
        </Button>
      </Form.Item>
      <Form.Item>
        <SubmitFormButton form={loginForm} text={AuthTranscription.FORM_LOGIN_TEXT} icon={<LoginOutlined />} /> &nbsp; O{' '}
        <Button type='link' onClick={() => NavigationUtils.handleAuthTypeAction(AuthType.REGISTER, true, null, location)}>
          {AuthTranscription.FORM_REGISTER_TEXT}
        </Button>
      </Form.Item>
    </Form>
  );
};
export default LoginForm;
